import React from 'react';

const TermsOfService = () => {
  return (
    <div className="terms-of-service">
      <h1>Terms of Service for DataSpeak LLC</h1>
      <p className="effective-date"><strong>Effective Date:</strong> 04/02/2025</p>
      <p className="intro">
        These Terms of Service (“Terms”) govern your access to and use of the SaaS-based AI product (“Service”) provided by DataSpeak LLC (“DataSpeak,” “we,” “us,” or “our”), a company registered in the State of Georgia.
      </p>
      <p>You agree to be bound by these Terms by accessing or using our Service. If you disagree, do not use the Service.</p>

      <PolicySection title="1. Acceptance of Terms">
        <p>By registering for, accessing, or using the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy.</p>
      </PolicySection>

      <PolicySection title="2. Eligibility">
        <p>You must be at least 18 years old and have the legal capacity to enter into these Terms. If you are using the Service on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.</p>
      </PolicySection>

      <PolicySection title="3. User Accounts">
        <p>You must provide accurate and complete information when creating an account. You are responsible for maintaining the confidentiality of your account credentials and all activities under your account. Please notify us immediately of any unauthorized use or security breach.</p>
      </PolicySection>

      <PolicySection title="4. License and Permitted Use">
        <p>DataSpeak grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for your internal business purposes, subject to these Terms. </p>
        <p>You may not:
          <ul>
            <li>Reverse engineer, decompile or disassemble the Service.</li>
            <li>Use the Service for unlawful, harmful, or abusive purposes.</li>
            <li>Resell, sublicense, or otherwise transfer the Service to any third party without our written consent.</li>
          </ul>
        </p>
      </PolicySection>

      <PolicySection title="5. Fees and Payment">
        <p>Access to certain features may require payment of fees as described at the time of purchase or subscription. All payments are non-refundable except as required by law or as expressly stated in these Terms. We reserve the right to change our fees with notice to you.</p>
      </PolicySection>

      <PolicySection title="6. Intellectual Property">
        <p>All intellectual property rights in the Service, including software, content, and trademarks, are owned by DataSpeak or its licensors. You retain ownership of any data or content you submit to the Service but grant DataSpeak a license to use, host, and process such data as necessary to provide the Service.</p>
      </PolicySection>

      <PolicySection title="7. User Content and Feedback">
        <p>You are responsible for all content you upload or input into the Service. You grant DataSpeak a worldwide, royalty-free license to use feedback or suggestions you provide to improve the Service.</p>
      </PolicySection>

      <PolicySection title="8. Prohibited Conduct">
        <p>You agree not to:
          <ul>
            <li>Use the Service in violation of any applicable law or regulation.</li>
            <li>Attempt to gain unauthorized access to the Service or related systems.</li>
            <li>Interfere with or disrupt the integrity or performance of the Service.</li>
          </ul>
        </p>
      </PolicySection>

      <PolicySection title="9. Service Availability and Support">
        <p>We strive to maintain the availability of the Service but do not guarantee uninterrupted or error-free operation. Support is provided as described on our website or in your subscription plan.</p>
      </PolicySection>

      <PolicySection title="10. Termination">
        <p>We may suspend or terminate your access to the Service at any time, with or without notice, for violating these Terms or for any other reason. Your right to use the Service ceases immediately upon termination. Sections regarding intellectual property, disclaimers, and limitation of liability survive termination.</p>
      </PolicySection>

      <PolicySection title="11. Disclaimers and Limitation of Liability">
        <p>The Service is provided “AS IS” and “AS AVAILABLE” without warranties of any kind, express or implied. DataSpeak is not liable for any indirect, incidental, special, consequential, or punitive damages or any loss of profits or revenues arising from your use of the Service.</p>
      </PolicySection>

      <PolicySection title="12. Indemnification">
        <p>You agree to indemnify and hold harmless DataSpeak, its affiliates, and their respective officers, directors, employees, and agents from any claims, damages, liabilities, and expenses arising from your use of the Service or violation of these Terms.</p>
      </PolicySection>

      <PolicySection title="13. Governing Law">
        <p>These Terms are governed by the laws of the State of Georgia without regard to its conflict of law principles. Any disputes will be resolved in the state or federal courts located in Georgia.</p>
      </PolicySection>

      <PolicySection title="14. Changes to Terms">
        <p>We may update these Terms from time to time. We will notify you of material changes by posting the updated Terms on our website. Continued use of the Service after changes constitute acceptance of the new Terms.</p>
      </PolicySection>

      <PolicySection title="15. Contact Information">
        <div className="contact-info">
          <p>For questions about these Terms, contact:</p>
          <p>DataSpeak LLC<br />
          info@dataspeak.co</p>
        </div>
      </PolicySection>

      <style jsx>{`
        .terms-of-service {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.6;
          color: #000;
        }

        h1 {
          color: #E07C2D;
          font-size: 2rem;
          margin-bottom: 0.5rem;
          font-weight: 700;
        }

        .effective-date {
          font-weight: 600;
          margin-bottom: 2rem;
          color: #666;
        }

        .intro {
          margin-bottom: 2rem;
        }

        h2 {
          color: #E07C2D;
          font-size: 1.5rem;
          margin: 2.5rem 0 1rem 0;
          font-weight: 600;
        }

        ul {
          padding-left: 1.5rem;
          margin: 1rem 0;
        }

        li {
          margin-bottom: 0.5rem;
          color: #000;
        }

        strong {
          color: #E07C2D;
          font-weight: 600;
        }

        .contact-info {
          background-color: rgba(224, 124, 45, 0.08);
          padding: 1.25rem;
          border-radius: 6px;
          margin: 1.5rem 0;
          border-left: 3px solid #E07C2D;
        }

        @media (max-width: 768px) {
          .terms-of-service {
            padding: 1.5rem;
          }

          h1 {
            font-size: 1.75rem;
          }

          h2 {
            font-size: 1.3rem;
          }
        }
      `}</style>
    </div>
  );
};

const PolicySection = ({ title, children }) => {
  return (
    <section>
      <h2>{title}</h2>
      {children}
    </section>
  );
};

export default TermsOfService;